import React, { useState, useEffect } from 'react';
import { useTheme } from './ThemeContext';
import { Icons } from './icons';
import Questionnaire, { resumeIndex, readLocalDraft, clearLocalDraft } from './Questionnaire';
import { saveProfile as apiSaveProfile, changePassword as apiChangePassword, activatePromoCode } from './api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const POPULAR_PROTEINS = [
  'Курица', 'Индейка', 'Говядина', 'Рыба белая', 'Рыба красная', 'Яйца', 'Творог', 'Сыр'
];

function Profile({ onSave, initialData, getAuth, onPromoActivated }) {
  const { isDark, toggleTheme, colors } = useTheme();
  const [formData, setFormData] = useState(() => {
    // Приоритет: свежие данные с сервера (initialData) > локальная копия (localStorage) > дефолт.
    // Раньше localStorage имел приоритет — из-за этого показывались устаревшие/пустые показатели
    // и после сохранения могли перезаписать серверный профиль. Теперь сервер всегда главный.
    //
    // Отличаем «серверный профиль» от «заглушки до загрузки»: серверный loadProfile ВСЕГДА
    // возвращает расширенные поля (weightHistory, waist, etc). Если их нет — данных с сервера
    // ещё не пришло, поэтому берём локальную копию, чтобы не потерять историю из localStorage.
    const isServerLoaded = initialData && Array.isArray(initialData.weightHistory);
    const base = isServerLoaded
      ? initialData
      : (() => {
          const saved = localStorage.getItem('userProfile');
          if (saved) { try { return JSON.parse(saved); } catch (e) { return null; } }
          return null;
        })() || initialData || { height: 170, weight: 80, age: 35, gender: 'мужчина', activity: 'сидячий' };
    return {
      height: base.height || 170,
      weight: base.weight || 80,
      age: base.age || 35,
      gender: base.gender || 'мужчина',
      activity: base.activity || 'сидячий',
      favoriteProteins: base.favoriteProteins || [],
      excludedProteins: base.excludedProteins || [],
      weightHistory: base.weightHistory || [],
      waist: base.waist || null,
      targetWeight: base.targetWeight || null,
      dailyWater: base.dailyWater || 2000,
      sleepHours: base.sleepHours || 8,
      mealsPerDay: base.mealsPerDay || 3,
      // Опросник едет вместе с профилем (тот же saveProfile), отдельного файла и
      // эндпоинта нет. Здесь он только протаскивается — правит его экран опроса.
      questionnaire: base.questionnaire || null,
    };
  });

  const [customProtein, setCustomProtein] = useState('');
  const [customExcluded, setCustomExcluded] = useState('');
  const [newWeightDate, setNewWeightDate] = useState(new Date().toISOString().split('T')[0]);
  const [newWeightValue, setNewWeightValue] = useState('');
  const [weightHistory, setWeightHistory] = useState(formData.weightHistory || []);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [editingWeightDate, setEditingWeightDate] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showFavModal, setShowFavModal] = useState(false);
  const [showExcModal, setShowExcModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  // Опросник: экран, подтверждение повторного прохождения и текущие ответы
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showQuestionnaireRepeat, setShowQuestionnaireRepeat] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(() => formData.questionnaire || null);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [currentTier, setCurrentTier] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.tier || 'basic'; } catch(e) {}
    return 'basic';
  });
  const [currentTierName, setCurrentTierName] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.tierName || null; } catch(e) {}
    return null;
  });
  const [currentTierExpires, setCurrentTierExpires] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.tierExpiresAt || null; } catch(e) {}
    return null;
  });
  // Индивидуальные лимиты тарифа (для custom-тарифа из промокода)
  const [currentSkLimit, setCurrentSkLimit] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.speechkitLimit || null; } catch(e) {}
    return null;
  });
  const [currentDsLimit, setCurrentDsLimit] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.deepseekLimit || null; } catch(e) {}
    return null;
  });
  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [passwordNew, setPasswordNew] = useState('');
  const [passwordNew2, setPasswordNew2] = useState('');
  const [showPassCur, setShowPassCur] = useState(false);
  const [showPassNew, setShowPassNew] = useState(false);
  const [showPassNew2, setShowPassNew2] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [mentorId] = useState(formData.mentorId || '');
  const [waistHistory, setWaistHistory] = useState(formData.waistHistory || []);
  const [showWaistModal, setShowWaistModal] = useState(false);
  const [newWaistDate, setNewWaistDate] = useState(new Date().toISOString().split('T')[0]);
  const [newWaistValue, setNewWaistValue] = useState('');
  const [editingWaistDate, setEditingWaistDate] = useState(null);
  const [advanced, setAdvanced] = useState({
    targetWeight: formData.targetWeight || null,
    dailyWater: formData.dailyWater || 2000,
    sleepHours: formData.sleepHours || 8,
    dailySteps: formData.dailySteps || 10000,
    runKm: formData.runKm || 0,
    runMin: formData.runMin || 0,
  });

  // Опросник сохраняется ТОЛЬКО как опросник (разделено 2026-09-12 по решению владельца).
  // Раньше сюда отправлялся весь профиль ({...formData, ...advanced}) — и кнопка опроса,
  // и сохранение прогресса тем самым трогали рост, вес, талию и историю. Теперь уходит
  // ровно один объект { questionnaire }, остальные поля профиля не участвуют вообще:
  // сервер накладывает присланное на существующее слиянием, поэтому профиль не меняется.
  // Запись в localStorage про профиль тоже убрана — опрос живёт отдельно.
  const handleQuestionnaireSave = async (payload, stayOpen = false) => {
    const nextQuestionnaire = payload;
    setQuestionnaire(nextQuestionnaire);
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user && user.login && user.token) {
      try {
        await apiSaveProfile(user.login, user.token, { questionnaire: nextQuestionnaire });
      } catch (err) {
        console.error('Ошибка сохранения опросника:', err);
        // Не глотаем молча: на экране опроса покажется, что прогресс не сохранился.
        if (!stayOpen) throw err;
      }
    }
    if (!stayOpen) clearLocalDraft();
    // ВАЖНО: onSave здесь НЕ вызываем намеренно. В App.js он делает
    // setUserProfile(p) + calculateNorms(p) — то есть ждёт ПОЛНЫЙ профиль.
    // Передать туда опросник ({answers, skipped, progress...}) значило бы
    // подменить профиль опросником и обнулить суточные нормы (NaN).
    // Опрос обновляем только в своём состоянии (setQuestionnaire выше).
  };

  // Черновик при закрытии крестиком: не завершает опрос, только запоминает,
  // докуда человек дошёл.
  const handleQuestionnaireProgress = (payload) => handleQuestionnaireSave(payload, true);

  // Что показать при открытии опроса. Приоритет:
  //  1) состояние компонента — сюда попадает и серверный опрос, и только что сохранённое;
  //  2) серверное значение из initialData напрямую — на случай, когда опрос
  //     сохранили в облако с другого устройства и Profile ещё не пересобрался;
  //  3) локальный черновик — если закрыли вкладку, а сеть не успела.
  const questionnaireForOpen = (() => {
    if (questionnaire) return questionnaire;
    if (initialData && initialData.questionnaire) return initialData.questionnaire;
    return readLocalDraft();
  })();

  const handleSubmit = async () => {
    const fullProfile = { ...formData, ...advanced, questionnaire, mentorId };
    localStorage.setItem('userProfile', JSON.stringify(fullProfile));
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user && user.login && user.token) {
      try {
        await apiSaveProfile(user.login, user.token, fullProfile);
      } catch (err) { console.error('Ошибка сохранения профиля на сервер:', err); }
    }
    if (onSave) onSave(fullProfile);
    setToast('Профиль сохранён!');
    setTimeout(() => setToast(null), 3000);
  };

  // Уведомление-тост живёт ровно 3 секунды. setTimeout выше задаёт закрытие;
  // этот эффект страхует случаи, когда тост перезаписывается — таймер предыдущего
  // сбрасывается вместе с ним, и новый отсчёт идёт от свежего сообщения.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const addFavoriteProtein = (protein) => {
    if (protein && !formData.favoriteProteins.includes(protein)) {
      setFormData(prev => ({ ...prev, favoriteProteins: [...prev.favoriteProteins, protein] }));
    }
    setCustomProtein('');
  };
  const removeFavoriteProtein = (protein) => setFormData(prev => ({ ...prev, favoriteProteins: prev.favoriteProteins.filter(p => p !== protein) }));
  const addExcludedProtein = (protein) => {
    if (protein && !formData.excludedProteins.includes(protein)) {
      setFormData(prev => ({ ...prev, excludedProteins: [...prev.excludedProteins, protein] }));
    }
    setCustomExcluded('');
  };
  const removeExcludedProtein = (protein) => setFormData(prev => ({ ...prev, excludedProteins: prev.excludedProteins.filter(p => p !== protein) }));

  // Вес
  const addWeightEntry = () => {
    if (!newWeightValue || isNaN(newWeightValue) || newWeightValue < 30 || newWeightValue > 300) { alert('Введите корректный вес (30-300 кг)'); return; }
    let updated;
    if (editingWeightDate) {
      updated = weightHistory.map(e => e.date === editingWeightDate ? { date: newWeightDate, weight: parseFloat(newWeightValue) } : e);
    } else {
      const newEntry = { date: newWeightDate, weight: parseFloat(newWeightValue) };
      updated = [newEntry, ...weightHistory].filter((e,i,s) => i === s.findIndex(x => x.date === e.date));
    }
    updated.sort((a,b) => b.date.localeCompare(a.date));
    setWeightHistory(updated);
    setFormData(prev => ({ ...prev, weight: updated[0].weight, weightHistory: updated }));
    setNewWeightValue('');
    setEditingWeightDate(null);
  };
  const removeWeightEntry = (date) => {
    const updated = weightHistory.filter(e => e.date !== date);
    setWeightHistory(updated);
    setFormData(prev => ({ ...prev, weightHistory: updated, weight: updated.length > 0 ? updated[0].weight : prev.weight }));
  };
  const selectWeight = (entry) => {
    setNewWeightDate(entry.date);
    setNewWeightValue(String(entry.weight));
    setEditingWeightDate(entry.date);
  };

  // Талия
  const addWaistEntry = () => {
    if (!newWaistValue || isNaN(newWaistValue) || newWaistValue < 50 || newWaistValue > 200) { alert('Введите корректный обхват талии (50-200 см)'); return; }
    let updated;
    if (editingWaistDate) {
      updated = waistHistory.map(e => e.date === editingWaistDate ? { date: newWaistDate, waist: parseFloat(newWaistValue) } : e);
    } else {
      updated = [{ date: newWaistDate, waist: parseFloat(newWaistValue) }, ...waistHistory].filter((e,i,s) => i === s.findIndex(x => x.date === e.date));
    }
    updated.sort((a,b) => b.date.localeCompare(a.date));
    setWaistHistory(updated);
    setFormData(prev => ({ ...prev, waist: updated[0].waist, waistHistory: updated }));
    setNewWaistValue('');
    setEditingWaistDate(null);
  };
  const removeWaistEntry = (date) => {
    const updated = waistHistory.filter(e => e.date !== date);
    setWaistHistory(updated);
    setFormData(prev => ({ ...prev, waistHistory: updated, waist: updated.length > 0 ? updated[0].waist : prev.waist }));
  };
  const selectWaist = (entry) => {
    setNewWaistDate(entry.date);
    setNewWaistValue(String(entry.waist));
    setEditingWaistDate(entry.date);
  };

  const s = {
    page: { maxWidth: '420px', margin: '0 auto', minHeight: '100vh', background: colors.page, fontFamily: 'system-ui, sans-serif', paddingBottom: '20px' },
    header: { background: colors.header, padding: '20px' },
    headerLabel: { fontSize: '11px', color: colors.textMuted, marginBottom: '4px' },
    headerTitle: { fontSize: '18px', fontWeight: '600', color: '#fff' },
    content: { padding: '20px' },
    section: { marginTop: '24px', padding: '16px', background: colors.card, borderRadius: '16px' },
    sectionTitle: { fontSize: '15px', fontWeight: '600', color: colors.text, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' },
    field: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '14px', fontWeight: '500', color: colors.textSecondary, marginBottom: '8px' },
    input: { width: '100%', padding: '12px', fontSize: '16px', border: `1px solid ${colors.inputBorder}`, borderRadius: '12px', boxSizing: 'border-box', background: colors.inputBg, color: colors.text },
    select: { width: '100%', padding: '12px', fontSize: '14px', border: `1px solid ${colors.inputBorder}`, borderRadius: '12px', background: colors.inputBg, color: colors.text },
    radioGroup: { display: 'flex', gap: '20px' }, radioLabel: { color: colors.textSecondary },
    saveBtn: { width: '100%', padding: '14px', backgroundColor: '#1D9E75', color: 'white', border: 'none', borderRadius: '16px', fontSize: '16px', cursor: 'pointer', marginTop: '20px' },
    themeSection: { marginTop: '20px', padding: '16px', background: colors.card, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    themeLabel: { fontSize: '14px', fontWeight: '500', color: colors.textSecondary },
    themeToggle: { display: 'flex', alignItems: 'center', gap: '10px' }, themeIcon: { fontSize: '20px' },
    switch: { width: '50px', height: '26px', background: isDark ? '#1D9E75' : '#cbd5e1', borderRadius: '13px', cursor: 'pointer', position: 'relative', transition: 'background 0.3s' },
    switchKnob: { width: '22px', height: '22px', background: '#fff', borderRadius: '50%', position: 'absolute', top: '2px', left: isDark ? '26px' : '2px', transition: 'left 0.3s' },
    addRow: { display: 'flex', gap: '8px', marginTop: '12px' },
    addSelect: { flex: 2, padding: '12px', fontSize: '14px', border: `1px solid ${colors.inputBorder}`, borderRadius: '12px', background: colors.inputBg, color: colors.text },
    addInput: { flex: 2, padding: '12px', fontSize: '14px', border: `1px solid ${colors.inputBorder}`, borderRadius: '12px', background: colors.inputBg, color: colors.text },
    addBtn: { padding: '12px 16px', backgroundColor: '#1D9E75', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
    tagsContainer: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' },
    tag: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: isDark ? '#2a2a2a' : '#f0f4f8', border: `1px solid ${colors.border}`, borderRadius: '20px', fontSize: '13px', color: colors.text },
    tagRemove: { cursor: 'pointer', color: colors.textMuted, fontSize: '16px', lineHeight: '1', marginLeft: '4px' },
    emptyTags: { color: colors.textMuted, fontSize: '13px', fontStyle: 'italic', marginTop: '8px' }
  };

  return (
    <div style={s.page}>
      {toast && (
        <div style={{position:'fixed',top:16,left:'50%',transform:'translateX(-50%)',zIndex:2000,background:'#1D9E75',color:'white',padding:'12px 24px',borderRadius:14,fontSize:14,fontWeight:500,boxShadow:'0 4px 16px rgba(0,0,0,0.2)',cursor:'pointer',whiteSpace:'nowrap'}} onClick={() => setToast(null)}>
          <Icons.check size={16} color="#fff" /> {toast}
        </div>
      )}
      <div style={s.header}><div style={s.headerLabel}><Icons.profile size={14} color="#ffffff88" /> ПРОФИЛЬ</div><div style={s.headerTitle}>Ваши параметры</div></div>
      <div style={s.content}>
        <div style={{display:'flex', gap:10, marginBottom:20}}>
          <div style={{flex:1}}><label style={s.label}>Рост (см)</label><input type="number" style={s.input} value={formData.height || ''} onChange={(e) => setFormData(prev => ({...prev, height: e.target.value === '' ? '' : Number(e.target.value)}))} /></div>
          <div style={{flex:1}}><label style={s.label}>Вес (кг)</label><input type="number" style={s.input} value={formData.weight || ''} onChange={(e) => setFormData(prev => ({...prev, weight: e.target.value === '' ? '' : Number(e.target.value)}))} /></div>
          <div style={{flex:1}}><label style={s.label}>Возраст</label><input type="number" style={s.input} value={formData.age || ''} onChange={(e) => setFormData(prev => ({...prev, age: e.target.value === '' ? '' : Number(e.target.value)}))} /></div>
          <div style={{flex:1}}><label style={s.label}>Талия (см)</label><input type="number" style={s.input} value={formData.waist || ''} onChange={(e) => setFormData(prev => ({...prev, waist: e.target.value ? Number(e.target.value) : null}))} /></div>
        </div>
         <div style={s.section}>
          <div style={s.sectionTitle}><Icons.weight size={16} color={colors.textSecondary} /> История веса</div>
          {weightHistory.length > 0 && (
            <div style={{fontSize:'13px',color:colors.textMuted,marginBottom:'8px'}}>
              {(() => { const d = weightHistory[0].weight - weightHistory[weightHistory.length-1].weight; return d !== 0 ? `Изменение: ${d>0?'+':''}${d.toFixed(1)} кг` : 'Вес стабилен'; })()}
              {' · '}{weightHistory.length} записей
            </div>
          )}
          <button style={{ ...s.addBtn, width: '100%' }} onClick={() => setShowWeightModal(true)}>
            <Icons.weight size={16} color="#fff" /> {weightHistory.length > 0 ? 'Посмотреть / добавить' : 'Добавить вес'}
          </button>
        </div>

        {/* Weight History Modal */}
        {showWeightModal && (
          <Modal onClose={() => setShowWeightModal(false)} title={<><Icons.weight size={18} color={colors.textSecondary} /> История веса</>}>
              <div style={{display:'flex', gap:8, marginBottom:12}}>
                <input type="date" style={{flex:1, padding:12, fontSize:14, border:`1px solid ${colors.inputBorder}`, borderRadius:12, background:colors.inputBg, color:colors.text}} value={newWeightDate} onChange={e => { setNewWeightDate(e.target.value); setEditingWeightDate(null); }} max={new Date().toISOString().split('T')[0]} />
                <input type="number" style={{flex:1, padding:12, fontSize:14, border:`1px solid ${colors.inputBorder}`, borderRadius:12, background:colors.inputBg, color:colors.text}} placeholder="Вес (кг)" value={newWeightValue} onChange={e => { setNewWeightValue(e.target.value); setEditingWeightDate(null); }} onKeyPress={e => e.key==='Enter' && addWeightEntry()} min="30" max="300" step="0.1" />
              </div>
              <div style={{display:'flex', gap:8, marginBottom:16}}>
                <button style={{flex:1, padding:'10px', background:'#1D9E75', color:'white', border:'none', borderRadius:12, cursor:'pointer', fontSize:14, fontWeight:500}} onClick={addWeightEntry}>
                  {editingWeightDate ? 'Обновить' : 'Добавить'}
                </button>
                {editingWeightDate && (
                  <>
                    <button style={{flex:1, padding:'10px', background:'#e53935', color:'white', border:'none', borderRadius:12, cursor:'pointer', fontSize:14, fontWeight:500}} onClick={() => { if(window.confirm('Удалить эту запись?')) { removeWeightEntry(editingWeightDate); setEditingWeightDate(null); setNewWeightValue(''); } }}>Удалить</button>
                    <button style={{padding:'10px 16px', background:colors.border, color:colors.text, border:'none', borderRadius:12, cursor:'pointer', fontSize:14}} onClick={() => { setEditingWeightDate(null); setNewWeightValue(''); setNewWeightDate(new Date().toISOString().split('T')[0]); }}>Отмена</button>
                  </>
                )}
              </div>
              {(() => {
                const sorted = [...weightHistory].sort((a,b) => a.date.localeCompare(b.date));
                const current = sorted.length > 0 ? sorted[sorted.length - 1] : null;
                const first = sorted.length > 1 ? sorted[0] : null;
                const totalDiff = current && first ? current.weight - first.weight : 0;
                return (
                  <>
                    {sorted.length > 0 ? (
                      <>
                        <div style={{marginBottom:12}}>
                          <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={sorted.map(e => ({ ...e, label: new Date(e.date).toLocaleDateString('ru-RU', {day:'numeric',month:'short'}) }))} margin={{top:5,right:10,left:0,bottom:5}}>
                              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                              <XAxis dataKey="label" tick={{fontSize:11, fill:colors.textMuted}} />
                              <YAxis tick={{fontSize:11, fill:colors.textMuted}} domain={['auto','auto']} />
                              <Tooltip contentStyle={{borderRadius:12, border:'none', background:colors.card, color:colors.text}} formatter={v => [`${v} кг`, 'Вес']} labelFormatter={l => `Дата: ${l}`} />
                              <Line type="monotone" dataKey="weight" stroke="#1D9E75" strokeWidth={2} dot={{r:5, fill:'#1D9E75', cursor:'pointer'}} activeDot={{r:7, cursor:'pointer', onClick:(_,e) => selectWeight(e.payload)}} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:colors.page, borderRadius:12}}>
                          <div>
                            <div style={{fontSize:12, color:colors.textMuted}}>Текущий вес</div>
                            <div style={{fontSize:20, fontWeight:700, color:colors.text}}>{current.weight} <span style={{fontSize:14, fontWeight:400, color:colors.textMuted}}>кг</span></div>
                          </div>
                          {sorted.length >= 2 && (
                            <div style={{textAlign:'right'}}>
                              <div style={{fontSize:12, color:colors.textMuted}}>c {new Date(first.date).toLocaleDateString('ru-RU', {day:'numeric',month:'short',year:'numeric'})}</div>
                              <div style={{fontSize:16, fontWeight:600, color: totalDiff < 0 ? '#1D9E75' : totalDiff > 0 ? '#e53935' : colors.textMuted}}>
                                {totalDiff > 0 ? '+' : ''}{totalDiff.toFixed(1)} кг
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div style={{color:colors.textMuted, textAlign:'center', padding:20}}>Нет измерений. Добавьте первый вес.</div>
                    )}
                  </>
                );
              })()}
          </Modal>
        )}

        <div style={s.section}>
          <div style={s.sectionTitle}><Icons.waist size={16} color={colors.textSecondary} /> История талии</div>
          {waistHistory.length > 0 && (
            <div style={{fontSize:'13px',color:colors.textMuted,marginBottom:'8px'}}>
              {(() => { const d = waistHistory[0].waist - waistHistory[waistHistory.length-1].waist; return d !== 0 ? `Изменение: ${d>0?'+':''}${d.toFixed(1)} см` : 'Талия стабильна'; })()}
              {' · '}{waistHistory.length} записей
            </div>
          )}
          <button style={{ ...s.addBtn, width: '100%' }} onClick={() => setShowWaistModal(true)}>
            <Icons.waist size={16} color="#fff" /> {waistHistory.length > 0 ? 'Посмотреть / добавить' : 'Добавить обхват талии'}
          </button>
        </div>

        {/* Waist History Modal */}
        {showWaistModal && (
          <Modal onClose={() => setShowWaistModal(false)} title={<><Icons.waist size={18} color={colors.textSecondary} /> История талии</>}>
            <div style={{display:'flex', gap:8, marginBottom:12}}>
              <input type="date" style={{flex:1, padding:12, fontSize:14, border:`1px solid ${colors.inputBorder}`, borderRadius:12, background:colors.inputBg, color:colors.text}} value={newWaistDate} onChange={e => { setNewWaistDate(e.target.value); setEditingWaistDate(null); }} max={new Date().toISOString().split('T')[0]} />
              <input type="number" style={{flex:1, padding:12, fontSize:14, border:`1px solid ${colors.inputBorder}`, borderRadius:12, background:colors.inputBg, color:colors.text}} placeholder="Талия (см)" value={newWaistValue} onChange={e => { setNewWaistValue(e.target.value); setEditingWaistDate(null); }} min="50" max="200" step="0.1" />
            </div>
            <div style={{display:'flex', gap:8, marginBottom:16}}>
              <button style={{flex:1, padding:'10px', background:'#1D9E75', color:'white', border:'none', borderRadius:12, cursor:'pointer', fontSize:14, fontWeight:500}} onClick={addWaistEntry}>
                {editingWaistDate ? 'Обновить' : 'Добавить'}
              </button>
              {editingWaistDate && (<>
                <button style={{flex:1, padding:'10px', background:'#e53935', color:'white', border:'none', borderRadius:12, cursor:'pointer', fontSize:14, fontWeight:500}} onClick={() => { if(window.confirm('Удалить эту запись?')) { removeWaistEntry(editingWaistDate); setEditingWaistDate(null); setNewWaistValue(''); } }}>Удалить</button>
                <button style={{padding:'10px 16px', background:colors.border, color:colors.text, border:'none', borderRadius:12, cursor:'pointer', fontSize:14}} onClick={() => { setEditingWaistDate(null); setNewWaistValue(''); setNewWaistDate(new Date().toISOString().split('T')[0]); }}>Отмена</button>
              </>)}
            </div>
            {(() => {
              const sorted = [...waistHistory].sort((a,b) => a.date.localeCompare(b.date));
              const current = sorted.length > 0 ? sorted[sorted.length - 1] : null;
              const first = sorted.length > 1 ? sorted[0] : null;
              const totalDiff = current && first ? current.waist - first.waist : 0;
              return (<>
                {sorted.length > 0 ? (<>
                  <div style={{marginBottom:12}}>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={sorted.map(e => ({ ...e, label: new Date(e.date).toLocaleDateString('ru-RU', {day:'numeric',month:'short'}) }))} margin={{top:5,right:10,left:0,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                        <XAxis dataKey="label" tick={{fontSize:11, fill:colors.textMuted}} />
                        <YAxis tick={{fontSize:11, fill:colors.textMuted}} domain={['auto','auto']} />
                        <Tooltip contentStyle={{borderRadius:12, border:'none', background:colors.card, color:colors.text}} formatter={v => [`${v} см`, 'Талия']} labelFormatter={l => `Дата: ${l}`} />
                        <Line type="monotone" dataKey="waist" stroke="#FAC775" strokeWidth={2} dot={{r:5, fill:'#FAC775', cursor:'pointer'}} activeDot={{r:7, cursor:'pointer', onClick:(_,e) => selectWaist(e.payload)}} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:colors.page, borderRadius:12}}>
                    <div><div style={{fontSize:12, color:colors.textMuted}}>Текущая талия</div><div style={{fontSize:20, fontWeight:700, color:colors.text}}>{current.waist} <span style={{fontSize:14, fontWeight:400, color:colors.textMuted}}>см</span></div></div>
                    {sorted.length >= 2 && (<div style={{textAlign:'right'}}><div style={{fontSize:12, color:colors.textMuted}}>c {new Date(first.date).toLocaleDateString('ru-RU', {day:'numeric',month:'short',year:'numeric'})}</div><div style={{fontSize:16, fontWeight:600, color: totalDiff < 0 ? '#1D9E75' : totalDiff > 0 ? '#e53935' : colors.textMuted}}>{totalDiff > 0 ? '+' : ''}{totalDiff.toFixed(1)} см</div></div>)}
                  </div>
                </>) : (<div style={{color:colors.textMuted, textAlign:'center', padding:20}}>Нет измерений. Добавьте первый обхват.</div>)}
              </>);
            })()}
          </Modal>
        )}

        <div style={{display:'flex', gap:10, marginBottom:20}}>
          <div style={{flex:1}}><label style={s.label}>Пол</label>
            <select style={s.select} value={formData.gender} onChange={(e) => setFormData(prev => ({...prev, gender: e.target.value}))}>
              <option value="мужчина">Мужчина</option><option value="женщина">Женщина</option>
            </select>
          </div>
          <div style={{flex:1}}><label style={s.label}>Активность</label>
            <button style={{...s.input, textAlign:'left', cursor:'pointer', color:colors.text}} onClick={() => setShowActivityModal(true)}>
              {{'сидячий':'Сидячий','малоактивный':'Малоактивный','активный':'Активный','очень активный':'Очень активный','экстра-активный':'Экстра-активный'}[formData.activity]}
            </button>
          </div>
        </div>

        <div style={s.section}>
          <div style={s.sectionTitle}><Icons.protein size={16} color="#5DCAA5" /> Любимые белки</div>
          {formData.favoriteProteins.length > 0 && <div style={s.tagsContainer}>{formData.favoriteProteins.map(p => <span key={p} style={s.tag}>{p}<span style={s.tagRemove} onClick={() => removeFavoriteProtein(p)}><Icons.close size={10} /></span></span>)}</div>}
          <button style={{...s.addBtn, width:'100%', marginTop: formData.favoriteProteins.length > 0 ? 12 : 0}} onClick={() => setShowFavModal(true)}>
            {formData.favoriteProteins.length > 0 ? 'Изменить' : 'Добавить любимые белки'}
          </button>
        </div>

        <div style={s.section}>
          <div style={s.sectionTitle}><Icons.block size={16} color="#e53935" /> Исключённые белки</div>
          {formData.excludedProteins.length > 0 && <div style={s.tagsContainer}>{formData.excludedProteins.map(p => <span key={p} style={s.tag}>{p}<span style={s.tagRemove} onClick={() => removeExcludedProtein(p)}><Icons.close size={10} /></span></span>)}</div>}
          <button style={{...s.addBtn, width:'100%', marginTop: formData.excludedProteins.length > 0 ? 12 : 0}} onClick={() => setShowExcModal(true)}>
            {formData.excludedProteins.length > 0 ? 'Изменить' : 'Добавить исключённые белки'}
          </button>
        </div>

        {/* Activity Modal */}
        {showActivityModal && <Modal onClose={() => setShowActivityModal(false)} title="Активность">
          {[
            {id:'сидячий',label:'Сидячий',desc:'Сидячая работа, мало движения в течение дня',icon:<Icons.activitySitting size={20} color={colors.textSecondary} />},
            {id:'малоактивный',label:'Малоактивный',desc:'Лёгкая активность, прогулки 1-3 раза в неделю',icon:<Icons.activityWalk size={20} color={colors.textSecondary} />},
            {id:'активный',label:'Активный',desc:'Регулярные тренировки 3-5 раз в неделю',icon:<Icons.activityRun size={20} color={colors.textSecondary} />},
            {id:'очень активный',label:'Очень активный',desc:'Интенсивные тренировки, физическая работа',icon:<Icons.activityStrong size={20} color={colors.textSecondary} />},
            {id:'экстра-активный',label:'Экстра-активный',desc:'Две тренировки в день, профессиональный спорт',icon:<Icons.activityMax size={20} color={colors.textSecondary} />}
          ].map(a => (
            <button key={a.id} style={{width:'100%', padding:14, marginBottom:8, background: formData.activity===a.id?'#1D9E75':colors.card, color: formData.activity===a.id?'white':colors.text, border:`1px solid ${colors.border}`, borderRadius:12, cursor:'pointer', fontSize:15, textAlign:'left', display:'flex', alignItems:'center', gap:12}}
              onClick={() => { setFormData(prev => ({...prev, activity: a.id})); setShowActivityModal(false); }}>
              {a.icon}
              <span>
                <div style={{fontWeight:600}}>{a.label}</div>
                <div style={{fontSize:12, opacity:0.7, marginTop:2}}>{a.desc}</div>
              </span>
            </button>
          ))}
        </Modal>}

        {/* Favorite Proteins Modal */}
        {showFavModal && <Modal onClose={() => setShowFavModal(false)} title="Любимые белки">
          {POPULAR_PROTEINS.map(p => (
            <button key={p} style={{width:'100%', padding:12, marginBottom:6, background: formData.favoriteProteins.includes(p)?'#1D9E75':colors.card, color: formData.favoriteProteins.includes(p)?'white':colors.text, border:`1px solid ${colors.border}`, borderRadius:12, cursor:'pointer', fontSize:14, textAlign:'left'}}
              onClick={() => formData.favoriteProteins.includes(p) ? removeFavoriteProtein(p) : addFavoriteProtein(p)}>
              {formData.favoriteProteins.includes(p) ? <Icons.check size={14} color="#1D9E75" /> : null}{p}
            </button>
          ))}
          <div style={{display:'flex', gap:8, marginTop:12}}>
            <input type="text" style={{flex:1, ...s.addInput}} placeholder="Свой белок" value={customProtein} onChange={e => setCustomProtein(e.target.value)} onKeyPress={e => e.key==='Enter' && addFavoriteProtein(customProtein)} />
            <button style={s.addBtn} onClick={() => addFavoriteProtein(customProtein)}>+</button>
          </div>
        </Modal>}

        {/* Excluded Proteins Modal */}
        {showExcModal && <Modal onClose={() => setShowExcModal(false)} title="Исключённые белки">
          {POPULAR_PROTEINS.map(p => (
            <button key={p} style={{width:'100%', padding:12, marginBottom:6, background: formData.excludedProteins.includes(p)?'#e53935':colors.card, color: formData.excludedProteins.includes(p)?'white':colors.text, border:`1px solid ${colors.border}`, borderRadius:12, cursor:'pointer', fontSize:14, textAlign:'left'}}
              onClick={() => formData.excludedProteins.includes(p) ? removeExcludedProtein(p) : addExcludedProtein(p)}>
              {formData.excludedProteins.includes(p) ? <Icons.close size={14} color="#e53935" /> : null}{p}
            </button>
          ))}
          <div style={{display:'flex', gap:8, marginTop:12}}>
            <input type="text" style={{flex:1, ...s.addInput}} placeholder="Свой белок" value={customExcluded} onChange={e => setCustomExcluded(e.target.value)} onKeyPress={e => e.key==='Enter' && addExcludedProtein(customExcluded)} />
            <button style={s.addBtn} onClick={() => addExcludedProtein(customExcluded)}>+</button>
          </div>
        </Modal>}
        <div style={{display:'flex', justifyContent:'flex-end', marginTop:20}}>
          <div style={{display:'inline-flex', alignItems:'center', gap:8, background:colors.card, borderRadius:20, padding:'6px 14px', border:`1px solid ${colors.border}`}}>
            <span style={{fontSize:14, fontWeight:500, color:colors.textSecondary}}>Тема</span>
            <span style={{fontSize:15, cursor:'pointer', color: isDark ? '#8899bb' : '#FAC775', opacity: isDark ? 0.5 : 1}} onClick={() => isDark && toggleTheme()}>☀</span>
            <div style={{width:36, height:20, background: isDark ? '#1D9E75' : '#cbd5e1', borderRadius:10, cursor:'pointer', position:'relative', transition:'background 0.3s'}} onClick={toggleTheme}>
              <div style={{width:16, height:16, background:'#fff', borderRadius:'50%', position:'absolute', top:2, left: isDark ? 18 : 2, transition:'left 0.3s'}} />
            </div>
            <span style={{fontSize:15, cursor:'pointer', color: isDark ? '#8899bb' : '#556677', opacity: isDark ? 1 : 0.5}} onClick={() => !isDark && toggleTheme()}>☾</span>
          </div>
        </div>
        <button style={{...s.addBtn, width:'100%', marginTop:12, background: colors.card, color: colors.text, border: `1px solid ${colors.border}`}} onClick={() => setShowAdvancedModal(true)}>
          <Icons.edit size={16} /> Расширенные параметры
        </button>
        {/* Опросник — сразу после «Расширенных параметров» (решение владельца 2026-09-12).
            Пройден → сначала спрашиваем, проходить ли снова; иначе открываем сразу. */}
        <button style={{...s.addBtn, width:'100%', marginTop:12, background: colors.card, color: colors.text, border: `1px solid ${colors.border}`}}
          onClick={() => { (questionnaire && questionnaire.completed) ? setShowQuestionnaireRepeat(true) : setShowQuestionnaire(true); }}>
          <Icons.profile size={16} color="#1D9E75" /> Опрос
          {questionnaire && questionnaire.completed && (
            <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: colors.textMuted }}>пройден</span>
          )}
        </button>
        {/* Блок «Наставник» удалён 2026-09-10: привязка по mentorId больше не используется.
            Наставник теперь — тот, кто ведёт поток (это видно в чате). */}
        <button style={{...s.addBtn, width:'100%', marginTop:12, background: colors.card, color: colors.text, border: `1px solid ${colors.border}`}} onClick={() => { setShowPasswordModal(true); setPasswordError(''); setPasswordCurrent(''); setPasswordNew(''); setPasswordNew2(''); }}>
          <Icons.lock size={16} color="#FAC775" /> Поменять пароль
        </button>
        <button style={{...s.addBtn, width:'100%', marginTop:12, background: colors.card, color: colors.text, border: `1px solid ${colors.border}`}} onClick={() => { setShowPromoModal(true); setPromoCode(''); setPromoError(''); }}>
          <Icons.key size={16} color="#85B7EB" /> Тариф: {currentTierName || (currentTier === 'custom' ? 'Premium' : 'Стандартный')}
        </button>
        {/* Mentor Modal */}
        {/* Mentor Modal удалён 2026-09-10 — привязки по mentorId больше нет */}

        {/* Кнопка «Сохранить» — в самом низу профиля.
            Возвращена 2026-09-12: она стояла здесь с первой версии и пропала
            10.09 вместе с блоком «Наставник», когда убирали mentorId.
            Вызывает handleSubmit (профиль → localStorage → облако, тост 3с).
            Это ОБЩАЯ кнопка профиля — у опросника своя «Сохранить» внутри его экрана. */}
        <button style={s.saveBtn} onClick={handleSubmit}><Icons.save size={18} color="#fff" /> СОХРАНИТЬ</button>

        {/* Advanced Modal */}
        {showAdvancedModal && <Modal onClose={() => setShowAdvancedModal(false)} title="Расширенные параметры">
          <div style={s.field}>
            <label style={s.label}>Целевой вес (кг)</label>
            <input type="number" style={s.input} placeholder="Оставьте пустым, если нет цели" value={advanced.targetWeight || ''} onChange={e => setAdvanced(prev => ({...prev, targetWeight: e.target.value ? Number(e.target.value) : null}))} min="30" max="300" />
          </div>
          <div style={s.field}>
            <label style={s.label}>Воды в день (мл)</label>
            <input type="number" style={s.input} value={advanced.dailyWater || ''} onChange={e => setAdvanced(prev => ({...prev, dailyWater: e.target.value ? Number(e.target.value) : 0}))} min="500" max="5000" step="100" />
          </div>
          <div style={s.field}>
            <label style={s.label}>Сон (часов)</label>
            <input type="number" style={s.input} value={advanced.sleepHours || ''} onChange={e => setAdvanced(prev => ({...prev, sleepHours: e.target.value ? Number(e.target.value) : 0}))} min="4" max="12" step="0.5" />
          </div>
          <div style={s.field}>
            <label style={s.label}>Шагов в день</label>
            <input type="number" style={s.input} placeholder="Цель по шагам" value={advanced.dailySteps || ''} onChange={e => setAdvanced(prev => ({...prev, dailySteps: e.target.value ? Number(e.target.value) : 0}))} min="1000" max="50000" step="500" />
          </div>
          <div style={{display:'flex', gap:10}}>
            <div style={{flex:1}}>
              <label style={s.label}>Бег (км)</label>
              <input type="number" style={s.input} placeholder="Цель в км" value={advanced.runKm || ''} onChange={e => setAdvanced(prev => ({...prev, runKm: e.target.value ? Number(e.target.value) : 0}))} min="0" max="100" step="0.5" />
            </div>
            <div style={{flex:1}}>
              <label style={s.label}>Бег (мин)</label>
              <input type="number" style={s.input} placeholder="Цель в минутах" value={advanced.runMin || ''} onChange={e => setAdvanced(prev => ({...prev, runMin: e.target.value ? Number(e.target.value) : 0}))} min="0" max="300" step="5" />
            </div>
          </div>
        </Modal>}
        {/* Повторное прохождение опроса — модалка подтверждения (п. 7 ТЗ) */}
        {showQuestionnaireRepeat && (
          <Modal onClose={() => setShowQuestionnaireRepeat(false)} title="Опрос">
            <div style={{fontSize:15,color:colors.text,marginBottom:16,lineHeight:1.45}}>
              Вы уже проходили опрос. Пройти снова?
            </div>
            <div style={{display:'flex',gap:10}}>
              <button style={{flex:1,padding:12,background:colors.border,color:colors.text,border:'none',borderRadius:12,fontSize:14,fontWeight:500,cursor:'pointer'}}
                onClick={() => setShowQuestionnaireRepeat(false)}>Отмена</button>
              <button style={{flex:1,padding:12,background:'#1D9E75',color:'white',border:'none',borderRadius:12,fontSize:14,fontWeight:600,cursor:'pointer'}}
                onClick={() => { setShowQuestionnaireRepeat(false); setShowQuestionnaire(true); }}>Пройти снова</button>
            </div>
          </Modal>
        )}

        {/* Опросник — полноэкранный слой, как чат: своя шапка, прогресс и кнопки */}
        {showQuestionnaire && (
          <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:1100,display:'flex',justifyContent:'center',background:colors.page}}>
            <div style={{width:'100%',maxWidth:480,height:'100%'}}>
              <Questionnaire
                getAuth={getAuth}
                initialAnswers={(questionnaireForOpen && questionnaireForOpen.answers) || {}}
                initialSkipped={(questionnaireForOpen && questionnaireForOpen.skipped) || []}
                initialProgress={resumeIndex(questionnaireForOpen && questionnaireForOpen.progress)}
                initialCompleted={!!(questionnaireForOpen && questionnaireForOpen.completed)}
                onSave={handleQuestionnaireSave}
                onSaveProgress={handleQuestionnaireProgress}
                onClose={() => setShowQuestionnaire(false)}
              />
            </div>
          </div>
        )}

        {/* Promo Modal */}
        {showPromoModal && <Modal onClose={() => setShowPromoModal(false)} title={<><Icons.key size={18} color="#85B7EB" /> Тариф</>}>
          <div style={{marginBottom:12,padding:16,background:currentTier === 'custom' ? 'rgba(29,158,117,0.1)' : 'rgba(153,170,187,0.1)',borderRadius:12,textAlign:'center'}}>
            <div style={{fontSize:14,color:colors.textSecondary,marginBottom:4}}>Текущий тариф</div>
            <div style={{fontSize:22,fontWeight:700,color:currentTier === 'custom' ? '#1D9E75' : colors.text}}>
              {currentTierName || (currentTier === 'custom' ? '⭐ Premium' : 'Стандартный')}
            </div>
            <div style={{fontSize:12,color:colors.textMuted,marginTop:4}}>
              {currentTier === 'custom'
                ? (currentSkLimit || currentDsLimit
                    ? `Диктовка ${currentSkLimit ?? 15}/день, Анализ ${currentDsLimit ?? 50}/день`
                    : 'Индивидуальные лимиты')
                : 'Диктовка 15/день, Анализ 50/день'}
            </div>
            {currentTierExpires && (
              <div style={{fontSize:11,color:colors.textMuted,marginTop:2}}>Действует до {new Date(currentTierExpires).toLocaleDateString('ru-RU')}</div>
            )}
          </div>
          <div style={{fontSize:13,color:colors.textSecondary,marginBottom:8}}>Введите промокод для апгрейда:</div>
          <input type="text" placeholder="Промокод" value={promoCode} onChange={e => setPromoCode(e.target.value)} style={{width:'100%',padding:'12px 16px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box',marginBottom:12}} />
          {promoError && <div style={{padding:12,background:colors.error,color:colors.errorText,borderRadius:12,fontSize:13,marginBottom:12,textAlign:'center'}}>{promoError}</div>}
          <button style={{width:'100%',padding:14,background:'#1D9E75',color:'white',border:'none',borderRadius:14,fontSize:15,fontWeight:600,cursor:'pointer'}} onClick={async () => {
            setPromoError('');
            if (!promoCode.trim()) { setPromoError('Введите промокод'); return; }
            const auth = getAuth ? getAuth() : null;
            if (!auth) { setPromoError('Требуется авторизация'); return; }
            const data = await activatePromoCode(auth.login, auth.token, promoCode.trim());
            if (data.success) {
              setCurrentTier(data.tier);
              setCurrentTierName(data.tierName);
              setCurrentTierExpires(data.tierExpiresAt);
              setCurrentSkLimit(data.speechkitLimit ?? null);
              setCurrentDsLimit(data.deepseekLimit ?? null);
              setShowPromoModal(false);
              setToast('Тариф обновлён');
              // Обновляем в localStorage
              try {
                const u = JSON.parse(localStorage.getItem('currentUser'));
                u.tier = data.tier;
                u.tierName = data.tierName;
                u.tierExpiresAt = data.tierExpiresAt;
                u.speechkitLimit = data.speechkitLimit;
                u.deepseekLimit = data.deepseekLimit;
                localStorage.setItem('currentUser', JSON.stringify(u));
              } catch(e) {}
              // Сообщаем App.js: перечитать лимиты с сервера,
              // иначе в шапке останутся старые знаменатели (30/15 и 100/50).
              if (onPromoActivated) onPromoActivated();
            } else {
              setPromoError(data.error || 'Неверный промокод');
            }
          }}>Активировать</button>
        </Modal>}
        {showPasswordModal && <Modal onClose={() => setShowPasswordModal(false)} title={<><Icons.lock size={18} color="#FAC775" /> Поменять пароль</>}>
          <div style={{marginBottom:12,position:'relative'}}>
            <input type={showPassCur ? 'text' : 'password'} placeholder="Текущий пароль" value={passwordCurrent} onChange={e => setPasswordCurrent(e.target.value)} style={{width:'100%',padding:'12px 40px 12px 12px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box'}} />
            <button type="button" style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:4}} onClick={() => setShowPassCur(!showPassCur)}>
              {showPassCur ? <Icons.eyeOff size={18} color="#99aabb" /> : <Icons.eye size={18} color="#99aabb" />}
            </button>
          </div>
          <div style={{marginBottom:12,position:'relative'}}>
            <input type={showPassNew ? 'text' : 'password'} placeholder="Новый пароль (мин. 4 символа)" value={passwordNew} onChange={e => setPasswordNew(e.target.value)} style={{width:'100%',padding:'12px 40px 12px 12px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box'}} />
            <button type="button" style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:4}} onClick={() => setShowPassNew(!showPassNew)}>
              {showPassNew ? <Icons.eyeOff size={18} color="#99aabb" /> : <Icons.eye size={18} color="#99aabb" />}
            </button>
          </div>
          <div style={{marginBottom:16,position:'relative'}}>
            <input type={showPassNew2 ? 'text' : 'password'} placeholder="Повторите новый пароль" value={passwordNew2} onChange={e => setPasswordNew2(e.target.value)} style={{width:'100%',padding:'12px 40px 12px 12px',fontSize:14,border:`1px solid ${colors.inputBorder}`,borderRadius:12,background:colors.inputBg,color:colors.text,boxSizing:'border-box'}} />
            <button type="button" style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:4}} onClick={() => setShowPassNew2(!showPassNew2)}>
              {showPassNew2 ? <Icons.eyeOff size={18} color="#99aabb" /> : <Icons.eye size={18} color="#99aabb" />}
            </button>
          </div>
          {passwordError && <div style={{padding:12,background:colors.error,color:colors.errorText,borderRadius:12,fontSize:13,marginBottom:12,textAlign:'center'}}>{passwordError}</div>}
          <button style={{width:'100%',padding:14,background:'#1D9E75',color:'white',border:'none',borderRadius:14,fontSize:15,fontWeight:600,cursor:'pointer'}} onClick={async () => {
            setPasswordError('');
            if (!passwordCurrent || !passwordNew || !passwordNew2) { setPasswordError('Заполните все поля'); return; }
            if (passwordNew.length < 4) { setPasswordError('Новый пароль должен быть не менее 4 символов'); return; }
            if (passwordNew !== passwordNew2) { setPasswordError('Новые пароли не совпадают'); return; }
            const auth = getAuth ? getAuth() : null;
            if (!auth) return;
            const data = await apiChangePassword(auth.login, auth.token, passwordCurrent, passwordNew);
            if (data.success) {
              setShowPasswordModal(false);
              setToast('Пароль изменён. Войдите заново.');
              // Показываем уведомление 3 секунды, потом выходим (укладывается в 3с: тост успевает погаснуть)
              setTimeout(() => setToast(null), 3000);
              // Разлогиним — сессии сброшены на сервере
              setTimeout(() => {
                localStorage.removeItem('currentUser');
                window.location.reload();
              }, 3000);
            } else {
              setPasswordError(data.error || 'Ошибка смены пароля');
            }
          }}>Сменить пароль</button>
        </Modal>}
      </div>
    </div>
  );
}

function Modal({ onClose, title, children }) {
  const { colors } = useTheme();
  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={onClose}>
      <div style={{background:colors.card,borderRadius:20,padding:24,width:'90%',maxWidth:420,maxHeight:'75vh',overflowY:'auto',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{fontSize:18,fontWeight:600,color:colors.text}}>{title}</div>
          <button style={{background:'none',border:'none',cursor:'pointer',padding:4}} onClick={onClose}><Icons.close size={20} color={colors.text} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default Profile;